<script setup lang="ts">
import profilePhotoPlaceholder from '@/assets/img/profile-photo-placeholder.svg';
import AppAvatar from '@/components/base/AppAvatar.vue';
import AppButton from '@/components/base/AppButton.vue';
import AppLogo from '@/components/base/AppLogo.vue';
import AppTypography from '@/components/base/AppTypography.vue';
import ThemeToggle from '@/components/ThemeToggle.vue';
import { useAuthStore } from '@/modules/auth/store/auth';
import { useMobileStore } from '@/store/mobile';
import { storeToRefs } from 'pinia';

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const mobileStore = useMobileStore();
const { showMenu } = storeToRefs(mobileStore);
const { toggleMenu } = mobileStore;
const { signOut } = useAuth();
const { showConfirmDialog } = useConfirmDialog();

const driverName = computed(() => user.value?.account?.name || user.value?.full_name || 'Driver');

const menuItems = [
  {
    title: 'Orders',
    to: '/driver/orders',
    icon: 'mdi-truck-delivery-outline',
  },
  {
    title: 'Driver profile',
    to: '/driver/profile',
    icon: 'mdi-card-account-details-outline',
  },
];

const handleLogout = async () => {
  showConfirmDialog({
    title: 'Log out?',
    description: 'Are you sure you want to log out?',
    confirmText: 'Log out',
    confirmTextColor: 'error',
    cancelText: 'Cancel',
    hasCancel: true,
    confirm: async () => {
      await signOut({ callbackUrl: '/driver' });
      user.value = null;
      showMenu.value = false;
    },
  });
};
</script>

<template>
  <v-navigation-drawer v-model="showMenu" width="373" temporary data-testid="driver-sidebar">
    <div class="driver-sidebar">
      <div class="driver-sidebar__header">
        <AppLogo height="42" width="42" />
        <AppButton
          variant="text"
          size="small"
          rounded="full"
          color="-primary"
          class="pa-0 circle"
          @click="toggleMenu()"
        >
          <v-icon icon="mdi-close" size="20" />
        </AppButton>
      </div>

      <div class="driver-sidebar__profile">
        <AppAvatar :src="profilePhotoPlaceholder" :size="64" />
        <div class="driver-sidebar__identity">
          <AppTypography element="h1" type="title-1" class="text-truncate">
            {{ driverName }}
          </AppTypography>
          <AppTypography element="span" type="footnote-2" color="-tertiary" class="text-truncate">
            {{ user?.email || user?.phone_number || 'Driver account' }}
          </AppTypography>
        </div>
        <ThemeToggle class="driver-sidebar__theme" />
      </div>

      <AppTypography element="h5" type="headline-1" class="mb-3">Driver</AppTypography>
      <v-list class="py-0 mb-6">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.to"
          class="pa-0 pr-2"
          height="56"
          @click="showMenu = false"
        >
          <template #prepend>
            <v-icon :icon="item.icon" class="mr-3" size="22" color="-primary" />
          </template>
          <template #title>
            <AppTypography element="span" type="body-1" class="font-weight-regular">
              {{ item.title }}
            </AppTypography>
          </template>
          <template #append>
            <v-btn color="-tertiary" density="compact" icon="mdi-chevron-right" variant="text" />
          </template>
        </v-list-item>
      </v-list>

      <div class="driver-sidebar__footer">
        <v-list class="py-0">
          <v-list-item class="pa-0 pr-2" height="56" @click="handleLogout">
            <template #prepend>
              <v-icon icon="mdi-logout" class="mr-3" size="22" color="-primary" />
            </template>
            <template #title>
              <AppTypography element="span" type="body-1" class="font-weight-regular">Logout</AppTypography>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.v-navigation-drawer {
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  border: 1px solid rgb(var(--v-theme-stroke));
  overflow: hidden;
}

.driver-sidebar {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.driver-sidebar__header,
.driver-sidebar__profile {
  display: flex;
  align-items: center;
}

.driver-sidebar__header {
  justify-content: space-between;
  height: 58px;
}

.driver-sidebar__profile {
  gap: 14px;
  margin: 18px 0 32px;
}

.driver-sidebar__identity {
  min-width: 0;
  flex: 1;
}

.driver-sidebar__theme {
  flex-shrink: 0;
}

.driver-sidebar__footer {
  margin-top: auto;
}

.v-list-item {
  &:not(:last-child) {
    border-bottom: 1px solid rgb(var(--v-theme-stroke));
  }

  &:hover,
  &.v-list-item--active {
    :deep(.v-list-item__overlay) {
      opacity: 0;
    }
  }
}

@media (max-width: 768px) {
  .v-navigation-drawer {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
